<template>
  <Modal
    v-model="visible"
    class-name="confkey-modal--view view-form"
    :title="modalTitle"
    :width="650"
    :mask-closable="false"
    @on-visible-change="closeModal"
  >
    <Form ref="confKeyFormRef" :model="confKeyForm">
      <Row>
        <i-col span="24">
          <FormItem :label="$t('common.name')" prop="name" :required="!isDetail">
            <i-input v-model="confKeyForm.name" size="large" :disabled="isEdit || isDetail" />
          </FormItem>
          <!-- if conf key `type` is not passed in, provide a choice of confKey -->
          <FormItem v-if="!type" :label="$t('common.type')" prop="confKeyType" :required="!isDetail">
            <ConfKeyTypesSeletor
              v-model="confKeyForm.confKeyType"
              :category="category"
              :disabled="isEdit || isDetail"
              @onChange="changeConfKeyType"
            />
          </FormItem>
        </i-col>
      </Row>
    </Form>

    <!-- properties -->
    <PropertyForm
      ref="propertyFormRef"
      from="sources"
      :category="category"
      :type="confKeyType"
      :properties="confKeyProperties.default"
      :form="propertiesForm"
      :modalVisible="visible"
      :sameLevelFormValid="sameLevelFormValid"
    />

    <!-- advanced -->
    <section v-if="isCanAdvanced">
      <SinkBaseForm
        ref="advanceFormRef"
        :type="confKeyType"
        :form="advanceForm"
        @changeConnectionSelector="changeConnectionSelector"
      />
    </section>

    <!-- connectionSelector  -->
    <Form v-if="!isCanAdvanced && isShowConnectionSelector" :model="confKeyForm">
      <Row>
        <i-col span="12">
          <FormItem v-if="isCanConnection" :label="$t('configuration.connectionSelector')">
            <ConnectionSelector
              v-model="confKeyForm.connectionSelector"
              :type="confKeyType"
              @onChange="changeConnectionSelector"
            />
          </FormItem>
        </i-col>
      </Row>
    </Form>

    <div slot="footer">
      <Button type="text" @click="closeModal">{{ $t('common.cancel') }}</Button>
      <Button type="primary" @click="submit">{{ $t('common.submit') }}</Button>
      <Button
        type="primary"
        ghost
        :loading="isLoadingTest"
        class="btn-test-connection"
        @click="submit('testConnection')"
      >
        {{ $t('common.testConnection') }}
      </Button>
    </div>
  </Modal>
</template>

<script>
import nodeID from '@/mixins/nodeID'
import {
  getMetadataByCategory,
  getMetadataYamlByCategory,
  testConnectionByCategory,
  addSourceConfKey,
  addSinksConfKey,
} from '@/api/metadata'
import PropertyForm from '@/components/PropertyForm.vue'
import ConfKeyTypesSeletor from '@/components/ConfKeyTypesSeletor.vue'
import SinkBaseForm from '@/views/rules/components/SinkBaseForm.vue'
import ConnectionSelector from '@/components/ConnectionSelector.vue'
import { removeObjectEmptyValue, getPropertiesDefaultValues, setPropertyDefaultValue } from '@/utils/handleData'
import { RESOURCE_TYPE, DEFAULT_RESOURCE_TYPE } from '@/utils/constants'

export default {
  name: 'ConfKeyPeopertiesModal',

  components: {
    ConfKeyTypesSeletor,
    PropertyForm,
    SinkBaseForm,
    ConnectionSelector,
  },

  props: {
    category: { type: String, default: DEFAULT_RESOURCE_TYPE }, // sources | sinks | functions | operators
    type: { type: String, default: '' }, // mqtt、file and so on...
    confKeyName: { type: String, default: '' },
    modalVisible: { type: Boolean, default: false },
    isDetail: { type: Boolean, default: false },
    isEdit: { type: Boolean, default: false },
    isCanAdvanced: { type: Boolean, default: false }, // Whether you can edit the `advanced` options
    isShowConnectionSelector: { type: Boolean, default: false },
    title: { type: String, default: '' },
  },

  mixins: [nodeID],

  data() {
    return {
      RESOURCE_TYPE,
      loading: false,
      allConfKeysData: {},
      confKeyForm: {
        name: '',
        confKeyType: '', // the same with props `type`, use it when props 'type' is undefined
        connectionSelector: '',
      },
      confKeyProperties: {
        default: [],
      },
      propertiesForm: {},
      advanceForm: {},
      isLoadingTest: false,
      sameLevelFormValid: false,
    }
  },

  computed: {
    visible: {
      get() {
        return this.modalVisible
      },
      set(val) {
        this.$emit('update:modalVisible', val)
      },
    },

    confKeyType() {
      return this.type || this.confKeyForm.confKeyType
    },

    modalTitle() {
      if (this.title) {
        return this.title
      }
      // general title
      return this.isEdit ? this.$t('configuration.editConfKey') : this.$t('configuration.addConfKey')
    },

    // Only `edgex` and `mqtt` support connectionSelector
    isCanConnection() {
      return ['mqtt', 'edgex'].includes(this.confKeyType)
    },
  },

  watch: {
    modalVisible: {
      immediate: true,
      handler(newV) {
        if (newV && this.type) {
          this.getFormData()
        }
      },
    },
  },

  methods: {
    // change `confKey` type
    changeConfKeyType(type) {
      if (!type) return

      this.getFormData()
    },

    // when change 'connection selector', cover some `conf keys` propertie value to the 'connection selector' value
    changeConnectionSelector(connection) {
      const params = {
        nodeID: this.nodeID,
        name: this.confKeyType,
      }
      getMetadataYamlByCategory('connections', params).then((res) => {
        const { data: allConnectionData } = res

        // Override some of the current `proprties` values
        const currentConnectionData = allConnectionData[connection]
        this.propertiesForm = {
          ...this.propertiesForm,
          ...currentConnectionData,
        }
      })
    },

    // get conf key properties & data
    async getFormData() {
      try {
        this.loading = true
        const params = {
          nodeID: this.nodeID,
          name: this.confKeyType,
        }

        // get properties
        const { data: keyPropertiesData } = await getMetadataByCategory(this.category, params)
        const { properties } = keyPropertiesData
        let allProperties = properties?.default || (Array.isArray(properties) ? properties : [])
        // filter out the `connectionSelector` field. If required, subsequent api removal.
        allProperties = allProperties.filter((item) => item.name !== 'connectionSelector')

        // get conf key detail
        if (this.isEdit) {
          const { data: allConfKeysData } = await getMetadataYamlByCategory(this.category, params)
          this.allConfKeysData = allConfKeysData
          const currentConfKeyConfigData = this.allConfKeysData[this.confKeyName]

          let curtconnectionSelector = null
          if (this.isCanAdvanced) {
            this.confKeyForm = {
              name: this.confKeyName,
              confKeyType: this.type,
            }
            // when can edit `advanced` options
            const {
              omitIfEmpty,
              sendSingle,
              format,
              schemaName,
              schemaMessage,
              delimiter: delimited,
              dataTemplate,
              concurrency,
              bufferLength,
              enableCache,
              memoryCacheThreshold,
              maxDiskCache,
              cleanCacheAtStop,
              bufferPageSize,
              resendInterval,
              runAsync,
              connectionSelector,
              ...confKeyData
            } = currentConfKeyConfigData

            curtconnectionSelector = connectionSelector ? connectionSelector.split('.')[1] : ''
            this.advanceForm = {
              omitIfEmpty,
              sendSingle,
              format,
              schemaName,
              schemaMessage,
              delimited,
              dataTemplate,
              concurrency,
              bufferLength,
              enableCache,
              memoryCacheThreshold,
              maxDiskCache,
              cleanCacheAtStop,
              bufferPageSize,
              resendInterval,
              runAsync,
              connectionSelector: curtconnectionSelector,
            }

            this.propertiesForm = {
              ...confKeyData,
            }
          } else {
            const { connectionSelector, ...confKeyConfigData } = currentConfKeyConfigData
            curtconnectionSelector = connectionSelector ? connectionSelector.split('.')[1] : ''

            this.confKeyForm = {
              name: this.confKeyName,
              confKeyType: this.type,
              connectionSelector: curtconnectionSelector,
            }

            this.propertiesForm = {
              ...confKeyConfigData,
            }
          }

          // if `curtconnectionSelector` is selected, overwrite the values of `properties` with its values
          if (curtconnectionSelector) {
            this.changeConnectionSelector(curtconnectionSelector)
          }
        } else {
          this.propertiesForm = getPropertiesDefaultValues(allProperties)
        }
        // get confKey properties
        this.confKeyProperties.default = allProperties
          ? allProperties.map((property) => {
              const { control, type } = property
              // if Editing, take detail `value` overwrite the `default value`
              const editValue =
                this.allConfKeysData &&
                this.allConfKeysData[this.confKeyName] &&
                this.allConfKeysData[this.confKeyName][property.name]
              const defaultValue = editValue || property.default
              if (property.control === 'list' && (property.type === 'list_object' || property.type === 'object')) {
                property.properties = property.default
                property.templateData = setPropertyDefaultValue(control, type, defaultValue) // used for array item
              } else {
                property.default = setPropertyDefaultValue(control, type, defaultValue)
              }

              if (property.name === 'insecureSkipVerify') {
                if (this.allConfKeysData && this.allConfKeysData[property.name] === undefined) {
                  property.default = true
                }
              }
              return property
            })
          : []
      } finally {
        this.loading = false
      }
    },

    closeModal(val) {
      if (typeof val === 'boolean') {
        this.visible = val
      } else {
        this.visible = false
      }
      if (!this.visible) {
        this.$refs.confKeyFormRef.resetFields()
        this.$refs.propertyFormRef.resetFields()
        this.confKeyProperties = { default: [] }
        this.allConfKeysData = {}
        this.confKeyForm = {
          name: '',
          confKeyType: '',
        }
        this.propertiesForm = {}
        this.advanceForm = {}
      }
      if (!this.visible && this.$refs.advanceFormRef) {
        this.$refs.advanceFormRef.resetFields()
      }
    },

    // test connection
    async testConnection(type, body) {
      const data = {
        nodeID: this.nodeID,
        type,
      }
      this.isLoadingTest = true
      testConnectionByCategory(this.category, data, body)
        .then(() => {
          this.$Message.success(this.$t('common.TestConnectionSuccess'))
        })
        .finally(() => {
          this.isLoadingTest = false
        })
    },

    // add | edit conf key
    addConfKeyAPI(category) {
      const apiMap = new Map([
        [RESOURCE_TYPE.sources, addSourceConfKey],
        [RESOURCE_TYPE.sinks, addSinksConfKey],
      ])
      return (category && apiMap.get(category)) || apiMap.get('source')
    },
    async addOrEditConfKey(type, body) {
      const createConfKeyAPI = this.addConfKeyAPI(this.category)
      const { name } = this.confKeyForm
      createConfKeyAPI(this.nodeID, type, name, body).then(() => {
        this.visible = false
        this.$emit('submit', { name, ...body })
        if (this.isEdit) {
          this.$Message.success(this.$t('common.editSuccess'))
        } else {
          this.$Message.success(this.$t('common.addSuccess'))
        }
      })
    },

    async getSubmitData() {
      try {
        const validConfKeyFormRes = await this.$refs.confKeyFormRef.validate()
        this.sameLevelFormValid = validConfKeyFormRes

        // propertyForm validate
        const { validatePropertiesForm } = this.$refs.propertyFormRef
        await validatePropertiesForm()

        if (!validConfKeyFormRes) {
          const errorMsg = this.$t('common.fillContent')
          this.$Message.error(errorMsg)
          return Promise.reject(new Error(errorMsg))
        }

        const propertyFormData = await this.$refs.propertyFormRef.getFormData()
        const { name, ...body } = propertyFormData

        let bodyData = {}

        if (this.isCanAdvanced) {
          const advanceFormData = await this.$refs.advanceFormRef.getFormData()

          const { connectionSelector, delimited, ...restAdvanceFormData } = advanceFormData
          const curtConnectionSelector = connectionSelector ? `${this.confKeyType}.${connectionSelector}` : ''
          bodyData = {
            // confKeyType: this.confKeyType,
            ...body,
            ...restAdvanceFormData,
            ...(advanceFormData.format === 'delimited' && { delimiter: delimited }),
            connectionSelector: curtConnectionSelector,
          }
        } else {
          const { connectionSelector } = this.confKeyForm
          bodyData = {
            ...body,
          }
          if (connectionSelector) {
            bodyData = {
              ...bodyData,
              connectionSelector: `${this.confKeyType}.${connectionSelector}`,
            }
          }
        }

        const submitBodyData = removeObjectEmptyValue(bodyData)
        return Promise.resolve(submitBodyData)
      } catch (error) {
        console.error('error', error)
        return Promise.reject(error)
      }
    },

    /**
     * submit
     * @param {String} type submit | test
     */
    async submit(submitType) {
      try {
        const submitBodyData = await this.getSubmitData()

        if (submitType === 'testConnection') {
          this.testConnection(this.confKeyType, submitBodyData)
        } else {
          this.addOrEditConfKey(this.confKeyType, submitBodyData)
        }
      } catch (error) {
        console.error('error', error)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-test-connection {
  position: absolute;
  right: 20px;
}
</style>
